import { BUILT_PROJECT_FOLDER, PROJECT_PATH_PREFIX } from '../const.js';

/** Manifest format version written under `.dist/.genesys/baked-assets.json`. */
export const BAKED_ASSETS_MANIFEST_VERSION = 1 as const;

/** Project-relative path (posix) to the baked-asset remap manifest inside a dist folder. */
export const BAKED_ASSETS_MANIFEST_REL_PATH = '.genesys/baked-assets.json';

/** On-disk manifest mapping logical source asset paths to baked dist targets. */
export interface BakedAssetsManifest {
  version: typeof BAKED_ASSETS_MANIFEST_VERSION;
  /** `@project/.../source.ext` → `@project/.../target.ext` (e.g. PNG → KTX2, WAV → OGG). */
  mappings: Record<string, string>;
}

/** Normalizes logical asset paths for stable manifest lookup. */
export function normalizeLogicalAssetPath(assetPath: string): string {
  return assetPath.replace(/\\/g, '/');
}

/** True when the storage root is a built `.dist` output folder. */
export function isDistStorageRoot(storageRoot: string): boolean {
  const normalized = storageRoot.replace(/\\/g, '/');
  const segments = normalized.split('/').filter(Boolean);
  return segments.at(-1) === BUILT_PROJECT_FOLDER;
}

/** Validates and normalizes parsed manifest JSON. Returns null when invalid. */
export function parseBakedAssetsManifest(data: unknown): BakedAssetsManifest | null {
  if (!data || typeof data !== 'object') {
    return null;
  }

  const record = data as { version?: unknown; mappings?: unknown };
  if (record.version !== BAKED_ASSETS_MANIFEST_VERSION) {
    return null;
  }
  if (!record.mappings || typeof record.mappings !== 'object' || Array.isArray(record.mappings)) {
    return null;
  }

  const mappings: Record<string, string> = {};
  for (const [source, target] of Object.entries(record.mappings as Record<string, unknown>)) {
    if (typeof source !== 'string' || typeof target !== 'string') {
      continue;
    }
    const normalizedSource = normalizeLogicalAssetPath(source);
    const normalizedTarget = normalizeLogicalAssetPath(target);
    if (!normalizedSource || !normalizedTarget) {
      continue;
    }
    mappings[normalizedSource] = normalizedTarget;
  }

  return {
    version: BAKED_ASSETS_MANIFEST_VERSION,
    mappings,
  };
}

/**
 * In-memory index for build-time remaps. Used at resolve time to swap logical
 * source paths (e.g. `.png`) for baked targets (e.g. `.ktx2`) without editing
 * serialized references.
 */
export class BakedAssetManifestIndex {
  private readonly mappings: ReadonlyMap<string, string>;

  private constructor(mappings: ReadonlyMap<string, string>) {
    this.mappings = mappings;
  }

  public static empty(): BakedAssetManifestIndex {
    return new BakedAssetManifestIndex(new Map());
  }

  public static fromManifest(manifest: BakedAssetsManifest): BakedAssetManifestIndex {
    const mappings = new Map<string, string>();
    for (const [source, target] of Object.entries(manifest.mappings)) {
      mappings.set(normalizeLogicalAssetPath(source), normalizeLogicalAssetPath(target));
    }
    return new BakedAssetManifestIndex(mappings);
  }

  public get size(): number {
    return this.mappings.size;
  }

  /** Returns the baked logical path when mapped, otherwise the input path. */
  public remap(logicalPath: string): string {
    if (!logicalPath.startsWith(PROJECT_PATH_PREFIX)) {
      return logicalPath;
    }
    return this.mappings.get(normalizeLogicalAssetPath(logicalPath)) ?? logicalPath;
  }

  public toManifest(): BakedAssetsManifest {
    const mappings: Record<string, string> = {};
    for (const [source, target] of this.mappings.entries()) {
      mappings[source] = target;
    }
    return {
      version: BAKED_ASSETS_MANIFEST_VERSION,
      mappings,
    };
  }
}

/**
 * Applies baked-asset remapping when a manifest is active. Only `@project/` paths
 * are candidates; `@engine/` and absolute paths pass through unchanged.
 */
export function remapLogicalAssetPath(
  logicalPath: string,
  manifest: BakedAssetManifestIndex | null | undefined,
): string {
  if (!manifest || manifest.size === 0) {
    return logicalPath;
  }
  return manifest.remap(logicalPath);
}
